import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, CheckCircle, AlertCircle, Clock, Trash2, Download, Tag } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import uploadService from '../../services/uploadService';
import NavigationBar from '../../components/ui/NavigationBar';
import MobileTabBar from '../../components/ui/MobileTabBar';
import LoadingState from '../../components/ui/LoadingState';
import { Button } from '../../components/ui/Button';
import { cn } from '../../utils/cn';

export default function UploadCenter() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [uploads, setUploads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('training_material');
  const [description, setDescription] = useState('');
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadUploads();
  }, [user, navigate, profile]);

  const loadUploads = async () => {
    try {
      setLoading(true);
      const teamId = profile?.primaryTeamId || profile?.primary_team_id;
      
      if (!teamId) {
        setError('No team associated with account');
        return;
      }

      const [uploadsData, statsData] = await Promise.all([
        uploadService.getTeamUploads(teamId),
        uploadService.getUploadStats(teamId)
      ]);

      setUploads(uploadsData);
      setStats(statsData);
      setError('');
    } catch (err) {
      console.error('Error loading uploads:', err);
      setError('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
        'application/msword'
      ];

      if (!validTypes.includes(file.type)) {
        setError('Please upload PDF, DOCX, DOC, or TXT files only');
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setSelectedFile(file);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setError('');

      const teamId = profile?.primaryTeamId || profile?.primary_team_id;
      const tags = tagInput.split(',').map(t => t.trim()).filter(t => t);

      await uploadService.uploadFile(
        selectedFile,
        fileType,
        teamId,
        description,
        tags
      );

      // Reset form
      setSelectedFile(null);
      setDescription('');
      setTagInput('');
      setFileType('training_material');
      document.getElementById('file-input').value = '';

      // Reload uploads
      await loadUploads();
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (uploadId) => {
    if (!confirm('Are you sure you want to delete this upload?')) return;

    try {
      await uploadService.deleteUpload(uploadId);
      await loadUploads();
    } catch (err) {
      console.error('Delete error:', err);
      setError('Failed to delete upload');
    }
  };

  const handleDownload = async (upload) => {
    try {
      const blob = await uploadService.downloadFile(upload.file_path);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = upload.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      setError('Failed to download file');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-500 animate-spin" />;
      case 'failed':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return 'Processed';
      case 'processing':
        return 'Processing...';
      case 'failed':
        return 'Failed';
      default:
        return 'Pending';
    }
  };

  const getFileTypeLabel = (type) => {
    const labels = {
      script: 'Sales Script',
      training_material: 'Training Material',
      objection_guide: 'Objection Guide',
      other: 'Other'
    };
    return labels[type] || type;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return <LoadingState message="Loading upload center..." />;
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />
      
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Upload Center</h1>
          <p className="text-muted-foreground">
            Upload your company's sales scripts and training materials to customize your training experience
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Total Uploads</div>
              <div className="text-2xl font-bold">{stats.total}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Processed</div>
              <div className="text-2xl font-bold text-green-500">
                {stats.byStatus?.completed || 0}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Processing</div>
              <div className="text-2xl font-bold text-yellow-500">
                {stats.byStatus?.processing || 0}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Total Size</div>
              <div className="text-2xl font-bold">{formatFileSize(stats.totalSize)}</div>
            </div>
          </div>
        )}

        {/* Upload Form */}
        <div className="bg-card border border-border rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Upload New Material
          </h2>

          {error && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* File Type Selection */}
            <div>
              <label className="block text-sm font-medium mb-2">Material Type</label>
              <select
                value={fileType}
                onChange={(e) => setFileType(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              >
                <option value="training_material">Training Material</option>
                <option value="script">Sales Script</option>
                <option value="objection_guide">Objection Guide</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* File Input */}
            <div>
              <label className="block text-sm font-medium mb-2">File (PDF, DOCX, DOC, TXT - Max 10MB)</label>
              <input
                id="file-input"
                type="file"
                accept=".pdf,.docx,.doc,.txt"
                onChange={handleFileSelect}
                className="w-full px-4 py-2 rounded-lg border border-input bg-background file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-primary file:text-primary-foreground file:cursor-pointer"
              />
              {selectedFile && (
                <div className="mt-2 text-sm text-muted-foreground">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium mb-2">Description (Optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Briefly describe this material..."
                className="w-full px-4 py-2 rounded-lg border border-input bg-background resize-none"
                rows={3}
              />
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-2">Tags (Optional, comma-separated)</label>
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="e.g., cold-calling, objections, closing"
                className="w-full px-4 py-2 rounded-lg border border-input bg-background"
              />
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={uploading || !selectedFile}
              className="w-full"
            >
              {uploading ? 'Uploading...' : 'Upload Material'}
            </Button>
          </div>
        </div>

        {/* Uploads List */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-border">
            <h2 className="text-xl font-semibold">Your Uploaded Materials</h2>
          </div>

          {uploads.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No uploads yet. Upload your first material above to get started!</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {uploads.map((upload) => (
                <div key={upload.id} className="p-6 hover:bg-accent/50 transition-colors">
                  <div className="flex items-start gap-4">
                    <FileText className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <h3 className="font-semibold text-lg mb-1">{upload.file_name}</h3>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            <span className="px-2 py-1 bg-primary/10 text-primary rounded">
                              {getFileTypeLabel(upload.file_type)}
                            </span>
                            <span>{formatFileSize(upload.file_size)}</span>
                            <span>{new Date(upload.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {getStatusIcon(upload.processing_status)}
                          <span className="text-sm">{getStatusText(upload.processing_status)}</span>
                        </div>
                      </div>

                      {upload.description && (
                        <p className="text-sm text-muted-foreground mb-3">{upload.description}</p>
                      )}

                      {upload.tags && upload.tags.length > 0 && (
                        <div className="flex items-center gap-2 mb-3">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          <div className="flex flex-wrap gap-2">
                            {upload.tags.map((tag, idx) => (
                              <span key={idx} className="text-xs px-2 py-1 bg-accent rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {upload.processing_status === 'completed' && (
                        <div className="flex flex-wrap gap-2 text-sm">
                          {upload.created_script && (
                            <span className="text-green-600">
                              ✓ Added to Script Library
                            </span>
                          )}
                          {upload.created_training_module_id && (
                            <span className="text-green-600">
                              ✓ Training Module Created
                            </span>
                          )}
                        </div>
                      )}

                      {upload.processing_status === 'failed' && upload.processing_error && (
                        <div className="text-sm text-destructive mt-2">
                          Error: {upload.processing_error}
                        </div>
                      )}

                      <div className="flex gap-2 mt-4">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownload(upload)}
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(upload.id)}
                          className="text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <MobileTabBar />
    </div>
  );
}
